import React, { useEffect, useRef, useState } from "react";
import { IconBell } from "@tabler/icons-react";
import { timeAgo } from "../utilities/timeAgo";

interface NotificationItemProps {
  n: any;
  onClick?: () => void;
  showDot?: boolean;
  icon?: React.ElementType;
  iconClass?: string;
  typeLabel?: string;
}

/* ---------------------------------------------
   RENDER MESSAGE + URL
--------------------------------------------- */

const renderMessage = (message: string) => {
  if (!message) return null;

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = message.split(urlRegex);

  return parts.map((part, index) => {
    const isUrl = /^https?:\/\//.test(part);

    if (isUrl) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          download
          onClick={(e) => e.stopPropagation()}
          className="
            inline
            font-medium
            text-blue-600
            underline
            underline-offset-2
            break-all
            transition-colors
            hover:text-blue-800
          "
        >
          Download Error Sheet
        </a>
      );
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  n,
  onClick,
  showDot = true,
  icon,
  iconClass,
  typeLabel,
}) => {
  const isUnread = !(n.seen ?? n.adminSeen);

  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  const messageRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = messageRef.current;

    if (!element || !n.message) {
      setShowToggle(false);
      return;
    }

    const checkOverflow = () => {
      // Temporarily remove clamp to determine actual content height
      const wasExpanded = isExpanded;

      if (!wasExpanded) {
        setShowToggle(element.scrollHeight > element.clientHeight + 1);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [n.message, isExpanded]);

  const imageUrl = n.image?.startsWith("http")
    ? n.image
    : n.image
      ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${n.image}`
      : "";

  const NotificationIcon = icon || IconBell;

  return (
    <div
      onClick={onClick}
      className="
        relative
        w-full
        px-4
        py-3.5
        sm:px-5
        sm:py-4
        cursor-pointer
        hover:bg-slate-100/60
        transition-colors
      "
    >
      <div className="flex items-start gap-3">
        {/* =====================================
            IMAGE / ICON
        ===================================== */}

        <div className="shrink-0">
          {imageUrl ? (
            <div className="relative">
              <img
                src={imageUrl}
                alt={n.title || "Notification"}
                className="
                  w-10
                  h-10
                  sm:w-11
                  sm:h-11
                  rounded-[14px]
                  object-cover
                  border
                  border-slate-200
                  shadow-sm
                "
              />

              {isUnread && (
                <span
                  className="
                    absolute
                    -right-1
                    -top-1
                    w-3
                    h-3
                    rounded-full
                    bg-red-500
                    border-2
                    border-white
                  "
                />
              )}
            </div>
          ) : (
            <div
              className={`
                relative
                w-10
                h-10
                sm:w-11
                sm:h-11
                rounded-[14px]
                flex
                items-center
                justify-center
                border
                transition-all
                duration-300
                group-hover:scale-105

                ${
                  iconClass ||
                  `
                    ${
                      isUnread
                        ? "bg-[#FFF8D8] text-[#967400] border-[#FBD037]/30"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                    }
                  `
                }
              `}
            >
              <NotificationIcon size={19} stroke={1.8} />

              {isUnread && (
                <span
                  className="
                    absolute
                    -right-1
                    -top-1
                    w-3
                    h-3
                    rounded-full
                    bg-red-500
                    border-2
                    border-white
                  "
                />
              )}
            </div>
          )}
        </div>

        {/* =====================================
            CONTENT
        ===================================== */}

        <div className="min-w-0 flex-1">
          {/* Title row */}
          <div className="flex items-center gap-2 min-w-0">
            <h4
              className={`min-w-0 truncate text-[13px] sm:text-[14px] leading-5 tracking-[-0.01em] ${isUnread ? "font-semibold text-[#212121]" : "font-medium text-slate-700"}`}
            >
              {n.title}
            </h4>

            {typeLabel && (
              <span className="hidden sm:inline-flex shrink-0 px-2 py-[3px] rounded-full bg-slate-100 text-[9px] uppercase tracking-wider font-semibold text-slate-400">
                {typeLabel}
              </span>
            )}
          </div>

          {/* Message */}
          {n.message && (
            <div className="mt-0.5">
              <p
                ref={messageRef}
                className={`text-[12px] sm:text-[13px] leading-[1.45] text-slate-500 break-words ${!isExpanded ? "line-clamp-2" : ""}`}
              >
                {renderMessage(n.message)}
              </p>

              {showToggle && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded((prev) => !prev);
                  }}
                  className="mt-1 text-[10px] sm:text-[11px] font-semibold text-[#A17D00] hover:text-[#806400] transition-colors cursor-pointer"
                >
                  {isExpanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}

          {/* Bottom information */}
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[10px] sm:text-[11px] font-medium text-slate-400">
              {timeAgo(n.created_at)}
            </span>

            {isUnread && (
              <>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#A17D00]">
                  New
                </span>
              </>
            )}
          </div>
        </div>

        {/* =====================================
            UNREAD DOT
        ===================================== */}

        {showDot && isUnread && (
          <span
            className="
              absolute
              right-4
              top-1/2
              -translate-y-1/2
              w-2
              h-2
              rounded-full
              bg-red-500
              shadow-[0_0_0_4px_rgba(239,68,68,0.08)]
            "
          />
        )}
      </div>
    </div>
  );
};
